"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { notFound } from "next/navigation";
import { Spinner } from "@nextui-org/react";
import ProfileComponent from "../profile/ProfileComponent";
import useUser from "@/hooks/useUser";
import findUser from "@/actions/findUser";
import { ExtendedUser, ensureExtendedUser } from "@/types/encryption";
import { useNavigation } from "@/hooks/useNavigation";
import { PostDetailView } from "@/components/PostDetailView";

// Reserved paths that should not be treated as usernames
const RESERVED_PATHS = [
  'login',
  'register',
  'signup',
  'signin',
  'explore',
  'search',
  'notifications',
  'messages',
  'settings',
  'profile',
  'admin',
  'api',
  'auth',
  'help',
  'support',
  'about',
  'privacy',
  'terms',
  'contact',
  'home',
  'feed',
  'trending',
  'discover',
  'bookmarks',
  'lists',
  'moments',
  'topics',
  'events',
  'spaces',
  'communities',
  'replies',
  'status',
  'post',
  'posts',
  'user',
  'users',
  'public',
  'static',
  '_next',
  'favicon.ico',
  'robots.txt',
  'sitemap.xml'
];

export default function UserProfile({ params }: { params: { username: string } }) {
  console.log('🚀 UserProfile component rendered for username:', params.username);
  
  const router = useRouter();
  const { user: currentUser, isLoading } = useUser();
  const [profileUser, setProfileUser] = useState<ExtendedUser | null>(null);
  const {
    view,
    selectedPost,
    postId,
    username,
    navigateToPost,
    navigateToProfile,
    setSelectedPost,
    setSelectedProfile
  } = useNavigation();

  // Debug logging for navigation state
  console.log('🔍 Profile page navigation state:', { view, postId, username, selectedPost: !!selectedPost });

  // Check if this is a reserved path
  if (RESERVED_PATHS.includes(params.username.toLowerCase())) {
    notFound();
  }

  useEffect(() => {
    const loadUser = async () => {
      try {
        console.log('🔍 Loading user profile for username:', params.username);
        
        // Load the requested user's profile
        const userData = await findUser({ username: params.username });
        console.log('📦 User data received:', userData);
        
        if (!userData) {
          console.log('❌ No user data found, redirecting to 404');
          router.push("/404");
          return;
        }

        // Convert to ExtendedUser
        const extendedUser = ensureExtendedUser(userData as any);

        console.log('✅ Extended user created:', extendedUser);
        setProfileUser(extendedUser);
      } catch (error) {
        console.error('❌ Error loading user profile:', error);
        router.push("/404");
      }
    };

    if (!isLoading) {
      console.log('🚀 Starting user profile load...');
      loadUser();
    }
  }, [router, params.username, isLoading]);

  if (isLoading || !profileUser) {
    return (
      <div className="flex w-full justify-center py-10">
        <Spinner size="lg" />
      </div>
    );
  }

  // Convert currentUser to ExtendedUser if it exists
  const extendedCurrentUser = currentUser ? ensureExtendedUser({
    ...currentUser,
    notifications: [],
    conversations: [],
    following: [],
    followers: [],
    publicKey: null,
    privateKey: null,
    encryptionKey: null,
    encryptedData: null,
    posts: [], // Add missing posts property
    suspended: false, // Add missing suspended property
  }) : undefined;

  // Debug logging for render decision
  console.log('🎯 Profile page render decision:', {
    view,
    willShowPostDetail: view === 'post',
    postId,
    selectedPost: !!selectedPost
  });

  // Conditionally render based on navigation state
  if (view === 'post') {
    console.log('✅ Rendering PostDetailView');
    return (
      <PostDetailView
        initialPost={selectedPost}
        postId={postId!}
        username={username!}
        currentUser={extendedCurrentUser}
        onBack={() => navigateToProfile(params.username, profileUser)}
      />
    );
  }

  console.log('✅ Rendering ProfileComponent');
  return (
    <ProfileComponent
      user={profileUser}
      currentUser={extendedCurrentUser}
      showBackButton={false}
      onPostClick={(post) => {
        setSelectedPost(post);
        navigateToPost(post, []);
      }}
      onProfileClick={(username, profile) => {
        if (profile) {
          setSelectedProfile(profile);
        }
        navigateToProfile(username, profile);
      }}
    />
  );
}